/**
 * HTML template generators for common UI patterns
 */

/**
 * Generate loading spinner HTML
 */
export function loadingSpinner(): string {
  return `
    <div class="loading">
      <div class="spinner"></div>
    </div>
  `;
}

/**
 * Generate empty state HTML
 */
export function emptyState(options: {
  icon: string;
  title: string;
  message?: string;
  buttonText?: string;
  buttonId?: string;
}): string {
  const { icon, title, message, buttonText, buttonId } = options;
  return `
    <div class="empty-state">
      <i class="fas fa-${icon}"></i>
      <h3>${title}</h3>
      ${message ? `<p>${message}</p>` : ''}
      ${buttonText && buttonId ? `<button class="btn btn-primary mt-2" id="${buttonId}">${buttonText}</button>` : ''}
    </div>
  `;
}

/**
 * Generate error state HTML with retry button
 */
export function errorState(options: {
  title: string;
  retryId?: string;
}): string {
  const { title, retryId } = options;
  return `
    <div class="empty-state">
      <i class="fas fa-exclamation-circle"></i>
      <h3>${title}</h3>
      ${retryId ? `<button class="btn btn-primary mt-2" id="${retryId}">Retry</button>` : ''}
    </div>
  `;
}

/**
 * Generate save indicator HTML for auto-save fields
 * @param id - The indicator element ID (typically "indicator-{fieldName}")
 * @param style - Optional inline style
 */
export function saveIndicator(id: string, style?: string): string {
  const styleAttr = style ? ` style="${style}"` : '';
  return `<span class="save-indicator" id="${id}"${styleAttr}>Saved <i class="fas fa-check"></i></span>`;
}

/**
 * Options for range slider with "Any" checkbox
 */
export interface RangeSliderOptions {
  id: string;               // Base ID for the slider (e.g., "age", "distance", "height")
  label: string;            // Label text (e.g., "Age Range", "Max Distance")
  isAny: boolean;           // Whether "Any" is currently selected
  displayValue: string;     // Current display value (e.g., "18-99" or "Any")
  indicatorId: string;      // ID for the save indicator
  sliders: SliderConfig[];  // Array of slider configs (1 for single, 2 for range)
}

export interface SliderConfig {
  id: string;
  min: number;
  max: number;
  value: number;
  step?: number;
  dataField?: string;
  dataJsonPath?: string;
}

/**
 * Generate range slider with "Any" checkbox HTML
 * Pattern: [x] Label [Saved ✓]                    Value
 *          [========slider(s)========]
 */
export function rangeSliderWithAny(options: RangeSliderOptions): string {
  const { id, label, isAny, displayValue, indicatorId, sliders } = options;

  const slidersHtml = sliders.map(s => {
    const dataAttrs = [
      s.dataField ? `data-field="${s.dataField}"` : '',
      s.dataJsonPath ? `data-json-path="${s.dataJsonPath}"` : '',
    ].filter(Boolean).join(' ');

    return `
      <input
        type="range"
        id="${s.id}"
        class="input"
        min="${s.min}"
        max="${s.max}"
        ${s.step ? `step="${s.step}"` : ''}
        value="${s.value}"
        ${dataAttrs}
        ${isAny ? 'disabled' : ''}
      />
    `;
  }).join('');

  const sliderContainer = sliders.length > 1
    ? `<div class="flex gap-2">${slidersHtml}</div>`
    : slidersHtml;

  return `
    <div class="preference-section">
      <div class="preference-row">
        <div class="filter-label-with-any">
          <input type="checkbox" id="any-${id}" ${isAny ? 'checked' : ''} />
          <span>${label}</span>
          <span class="save-indicator" id="${indicatorId}">Saved <i class="fas fa-check"></i></span>
        </div>
        <span id="${id}-display" class="filter-value">${displayValue}</span>
      </div>
      ${sliderContainer}
    </div>
  `;
}
